import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Button,
  Modal,
  Slide,
  Chip,
  IconButton,
  Checkbox,
  CircularProgress,
  Alert,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import { SafeTextField } from "../../../common/Greens";
import LayerdIcon from "../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../icons/uiLucideIcons";
import PermissionGate from "../../../guards/PermissionGate";
import useSite from "../../../../hooks/user/useSite";
import useAdmin from "../../../../hooks/user/useAdmin";
import usePermissions from "../../../../hooks/usePermissions";
import { ExternalLogType } from "../../../../utils/constants";
import axios from "../../../../utils/axios/v1/userAxios";
import {
  useAlertIntegrations,
  ConfiguredDestinationsList,
  AlertIntegrationOverlays,
} from "./IntegrationsPage";
import { AlertVendorGroups } from "./integrationVendorGroups";
import { IntegrationBrandIcon } from "./integrationBrandIcon";
import { validateWebhookForm } from "../../../../utils/validation";
import { parseJsonSafe } from "../../../../utils/validation/patterns";
import { SaveButton, formSaveButtonSx } from "../../../common/Button";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";

const webhookTypes = [
  {
    id: "splunk",
    name: "Splunk",
    icon: "splunk",
    color: "#65A637",
    fields: ["url", "token", "index"],
    enabled: false,
    externalLogType: ExternalLogType.SPLUNK,
  },
  {
    id: "sumo_logic",
    name: "Sumo Logic",
    icon: "sumo",
    color: "#0078D4",
    fields: ["url", "token"],
    enabled: false,
    externalLogType: ExternalLogType.SUMO_LOGIC,
  },
  {
    id: "general",
    name: "General",
    icon: "general",
    color: "#6371F7",
    fields: ["url", "headers"],
    enabled: false,
    externalLogType: ExternalLogType.GENERAL,
  },
  {
    id: "cloudwatch",
    name: "Amazon CloudWatch",
    icon: "aws",
    color: "#FF9900",
    fields: ["region", "accessKey", "secretKey", "logGroup"],
    enabled: false,
    externalLogType: ExternalLogType.AMAZON_CLOUD_WATCH,
  },
  {
    id: "elastic",
    name: "Elastic Search",
    icon: "elastic",
    color: "#00BFB3",
    fields: ["url", "token", "index"],
    enabled: false,
    externalLogType: ExternalLogType.ELASTIC_SEARCH,
  },
  {
    id: "stackdriver",
    name: "Google Stackdriver",
    icon: "google",
    color: "#4285F4",
    fields: ["projectId", "credentials"],
    enabled: false,
    externalLogType: ExternalLogType.GOOGLE_STACK_DRIVER,
  },
  {
    id: "qradar",
    name: "IBM QRadar",
    icon: "ibm",
    color: "#054ADA",
    fields: ["url", "token"],
    enabled: false,
    externalLogType: ExternalLogType.IBM_QRADAR,
  },
  {
    id: "syslog",
    name: "Syslog",
    icon: "syslog",
    color: "#6B7280",
    fields: ["host", "port", "protocol"],
    enabled: false,
    externalLogType: ExternalLogType.SYSLOG,
  },
];

const WebhookCard = ({ webhook, onLogoClick, onStatusToggle, webhookIndex }) => {
  const isClickable = webhookIndex < 3;
  
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        backgroundColor: isClickable ? "#fff" : colors.neutral[100],
        border: `1px solid ${colors.neutral[200]}`,
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 140,
        opacity: isClickable ? 1 : 0.7,
      }}
    >
      <Box
        onClick={isClickable ? onLogoClick : undefined}
        sx={{
          cursor: isClickable ? "pointer" : "not-allowed",
          transition: "transform 0.2s",
          opacity: isClickable ? 1 : 0.5,
          "&:hover": {
            transform: isClickable ? "scale(1.05)" : "none",
          },
        }}
      >
        <IntegrationBrandIcon id={webhook.icon} size={48} />
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: "medium",
          color: isClickable ? colors.neutral[1000] : colors.neutral[600],
          mt: 2,
          textAlign: "center",
        }}
      >
        {webhook.name}
      </Typography>
      <Chip
        label={webhook.enabled ? "Enabled" : "Disabled"}
        size="small"
        onClick={isClickable ? onStatusToggle : undefined}
        sx={{
          mt: 1.5,
          height: 24,
          backgroundColor: webhook.enabled ? "#D1FAE5" : "#FEF3C7",
          color: webhook.enabled ? "#065F46" : "#92400E",
          fontSize: 11,
          fontWeight: "medium",
          cursor: isClickable ? "pointer" : "not-allowed",
          transition: "all 0.2s",
          "&:hover": {
            opacity: isClickable ? 0.8 : 1,
            transform: isClickable ? "scale(1.05)" : "none",
          },
        }}
      />
    </Box>
  );
};

const StatusSwitch = ({ checked, onChange, name }) => (
  <Box
    onClick={() => onChange && onChange({ target: { name, checked: !checked } })}
    sx={{
      width: 48,
      height: 26,
      borderRadius: "13px",
      backgroundColor: checked ? "#6371F7" : colors.neutral[300],
      position: "relative",
      cursor: "pointer",
      transition: "background-color 0.2s",
    }}
  >
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#fff",
        position: "absolute",
        top: 3,
        left: checked ? 25 : 3,
        transition: "left 0.2s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    />
  </Box>
);

const WebhookConfigDrawer = ({ open, onClose, webhook, sites = [], basisData = [], onSave }) => {
  const { getWebhookInfo, saveWebhookInfo, connectionTest, setWebhook } = useAdmin();
  const [enabled, setEnabled] = useState(webhook?.enabled || false);
  const [formValues, setFormValues] = useState({
    url: "",
    token: "",
    index: "",
    headers: "",
    region: "",
    accessKey: "",
    secretKey: "",
    logGroup: "",
    projectId: "",
    credentials: "",
    apiKey: "",
    site: "",
    host: "",
    port: "",
    protocol: "tcp",
  });
  const [selectedSites, setSelectedSites] = useState([]);
  const [verifyTls, setVerifyTls] = useState(true);
  const [testStatus, setTestStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const validation = useMemo(
    () => validateWebhookForm(webhook?.fields, formValues),
    [webhook?.fields, formValues],
  );

  const showFieldErr = (key) => (submitAttempted || touched[key]) && validation.fieldErrors[key];

  const formSnapshot = useMemo(
    () => ({ enabled, formValues, selectedSites, verifyTls }),
    [enabled, formValues, selectedSites, verifyTls],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(onClose, isDirty, {
    disabled: loading || saving,
  });

  useEffect(() => {
    if (open && webhook) {
      loadWebhookConfig();
    } else if (!open) {
      setFormValues({
        url: "",
        token: "",
        index: "",
        headers: "",
        region: "",
        accessKey: "",
        secretKey: "",
        logGroup: "",
        projectId: "",
        credentials: "",
        apiKey: "",
        site: "",
        host: "",
        port: "",
        protocol: "tcp",
      });
      setSelectedSites([]);
      setVerifyTls(true);
      setTestStatus(null);
      setError(null);
      setTouched({});
      setSubmitAttempted(false);
      setBaselineKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, webhook, basisData]);

  const loadWebhookConfig = async () => {
    if (!webhook?.externalLogType) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await getWebhookInfo(webhook.externalLogType);
      
      if (data && typeof data === "object") {
        setEnabled(data.enabled || false);
        setVerifyTls(data.verify_tls !== false);
        
        const newFormValues = { ...formValues };
        if (data.url) newFormValues.url = data.url;
        if (data.token) newFormValues.token = data.token;
        if (data.index) newFormValues.index = data.index;
        if (data.headers) {
          newFormValues.headers = typeof data.headers === "string" ? data.headers : JSON.stringify(data.headers);
        }
        if (data.region) newFormValues.region = data.region;
        if (data.accessKey) newFormValues.accessKey = data.accessKey;
        if (data.secretKey) newFormValues.secretKey = data.secretKey;
        if (data.logGroup) newFormValues.logGroup = data.logGroup;
        if (data.projectId) newFormValues.projectId = data.projectId;
        if (data.credentials) {
          newFormValues.credentials = typeof data.credentials === "string" ? data.credentials : JSON.stringify(data.credentials);
        }
        if (data.apiKey) newFormValues.apiKey = data.apiKey;
        if (data.site) newFormValues.site = data.site;
        if (data.host) newFormValues.host = data.host;
        if (data.port) newFormValues.port = data.port;
        if (data.protocol) newFormValues.protocol = data.protocol;
        
        setFormValues(newFormValues);
        
        if (data.sites && Array.isArray(data.sites)) {
          const enabledSites = data.sites
            .filter((site) => site.enable || site.enabled)
            .map((site) => site.site_id);
          setSelectedSites(enabledSites);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to load webhook configuration");
    } finally {
      setLoading(false);
      setBaselineKey((k) => k + 1);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const buildValidatedPayload = () => {
    const result = validateWebhookForm(webhook?.fields, formValues);
    if (!result.valid) {
      throw new Error(Object.values(result.fieldErrors)[0] || "Please fix validation errors");
    }

    const payload = { ...formValues, verify_tls: verifyTls };
    for (const key of ["headers", "credentials"]) {
      if (!webhook?.fields?.includes(key)) continue;
      const raw = String(formValues[key] ?? "").trim();
      if (!raw) {
        delete payload[key];
        continue;
      }
      const parsed = parseJsonSafe(raw);
      if (!parsed.ok) {
        throw new Error(`${getFieldLabel(key)}: invalid JSON (${parsed.message})`);
      }
      payload[key] = parsed.value;
    }
    return payload;
  };

  const handleSiteToggle = (siteId) => {
    setSelectedSites((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]
    );
  };

  const handleSelectAllSites = () => {
    const availableSites = basisData.length > 0 ? basisData : sites;
    if (selectedSites.length === availableSites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(availableSites.map((s) => s.site_id || s.id));
    }
  };

  const handleTestConnection = async () => {
    if (!webhook?.externalLogType) return;

    setSubmitAttempted(true);
    setTestStatus("testing");
    setError(null);

    try {
      const testPayload = buildValidatedPayload();
      const result = await connectionTest(webhook.externalLogType, testPayload);
      
      if (result) {
        setTestStatus("success");
        setTimeout(() => setTestStatus(null), 3000);
      } else {
        setTestStatus("error");
        setError("Connection test failed");
        setTimeout(() => {
          setTestStatus(null);
          setError(null);
        }, 3000);
      }
    } catch (err) {
      setTestStatus("error");
      setError(err.message || "Connection test failed");
      setTimeout(() => {
        setTestStatus(null);
        setError(null);
      }, 3000);
    }
  };

  const handleSave = async () => {
    if (!webhook?.externalLogType) return;

    setSubmitAttempted(true);
    const result = validateWebhookForm(webhook.fields, formValues);
    if (!result.valid) return;

    setSaving(true);
    setError(null);

    try {
      const availableSites = basisData.length > 0 ? basisData : sites;
      const savePayload = {
        enabled,
        verify_tls: verifyTls,
        sites: availableSites.map((site) => ({
          site_id: site.site_id || site.id,
          enable: selectedSites.includes(site.site_id || site.id),
        })),
      };

      Object.keys(formValues).forEach((key) => {
        if (!webhook.fields.includes(key) || !formValues[key]) return;
        if (key === "headers" || key === "credentials") {
          const parsed = parseJsonSafe(formValues[key]);
          if (!parsed.ok) {
            throw new Error(`${getFieldLabel(key)}: invalid JSON (${parsed.message})`);
          }
          savePayload[key] = parsed.value;
        } else {
          savePayload[key] = formValues[key];
        }
      });
      
      const success = await saveWebhookInfo(webhook.externalLogType, savePayload);
      
      if (success) {
        if (onSave) {
          onSave();
        }
        onClose();
      } else {
        setError("Failed to save webhook configuration");
      }
    } catch (err) {
      setError(err.message || "Failed to save webhook configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleEnabledChange = async (newEnabled) => {
    if (!webhook?.externalLogType) return;
    
    const previousEnabled = enabled;
    setEnabled(newEnabled);
    
    try {
      await setWebhook(webhook.externalLogType, newEnabled);
      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError(err.message || "Failed to update webhook status");
      setEnabled(previousEnabled);
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      url: "Service HTTP URL",
      token: "Service Token",
      index: "Index",
      headers: "Custom Headers (JSON)",
      region: "AWS Region",
      accessKey: "Access Key ID",
      secretKey: "Secret Access Key",
      logGroup: "Log Group Name",
      projectId: "Project ID",
      credentials: "Service Account Credentials (JSON)",
      apiKey: "API Key",
      site: "Datadog Site",
      host: "Syslog Host",
      port: "Syslog Port",
      protocol: "Protocol",
    };
    return labels[field] || field;
  };

  if (!webhook) return null;

  return (
    <>
    <Modal open={open} onClose={requestClose}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            outline: "none",
          }}
        >
          <ReactPerfectScrollbar>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}` }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1, overflow: "visible" }}>
                <IntegrationBrandIcon id={webhook.icon} size={48} />
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: "medium",
                    color: colors.neutral[1000],
                  }}
                >
                  {webhook.name} Configuration
                </Typography>
              </Stack>
              <IconButton onClick={requestClose}>
                <LayerdIcon icon={UI_ICONS.close} size={24} color="neutral" />
              </IconButton>
            </Stack>

            <Box sx={{ p: 4 }}>
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {!loading && (
                <Stack spacing={4}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography
                      sx={ { fontSize: 15, fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Enable {webhook.name} Logging
                    </Typography>
                    <StatusSwitch checked={enabled} onChange={() => handleEnabledChange(!enabled)} name="enabled" />
                  </Stack>

                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    backgroundColor: colors.neutral[50],
                    border: `1px solid ${colors.neutral[200]}`,
                  }}
                >
                  <Stack spacing={3}>
                    {webhook.fields?.map((field) => (
                      <SafeTextField
                        key={field}
                        label={getFieldLabel(field)}
                        size="small"
                        fullWidth
                        placeholder={`Enter ${getFieldLabel(field).toLowerCase()}`}
                        value={formValues[field] || ""}
                        error={Boolean(showFieldErr(field))}
                        helperText={showFieldErr(field) || undefined}
                        onBlur={() => setTouched((prev) => ({ ...prev, [field]: true }))}
                        onChange={(e) => handleFieldChange(field, e.target.value)}
                        multiline={field === "headers" || field === "credentials"}
                        minRows={field === "headers" || field === "credentials" ? 3 : 1}
                        type={field.includes("Key") || field === "token" ? "password" : "text"}
                        sx={
                          field === "headers" || field === "credentials" || field.includes("Key") || field === "token"
                            ? { "& input, & textarea": { fontFamily: "monospace" } }
                            : undefined
                        }
                      />
                    ))}
                    {webhook.fields?.includes("url") && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={ { fontSize: 14, color: colors.neutral[700] }}
                          >
                            Verify TLS certificate
                          </Typography>
                          <Typography
                            sx={ { fontSize: 12, color: colors.neutral[500] }}
                          >
                            Turn off only for self-signed endpoints (e.g. Splunk HEC default cert).
                          </Typography>
                        </Stack>
                        <StatusSwitch checked={verifyTls} onChange={() => setVerifyTls(!verifyTls)} name="verify_tls" />
                      </Stack>
                    )}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    backgroundColor: colors.neutral[50],
                    border: `1px solid ${colors.neutral[200]}`,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography
                      sx={ { fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Websites for external webhook logging
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={handleSelectAllSites}
                      sx={{
                        py: 1.5,
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                        cursor: "pointer",
                      }}
                    >
                      <Checkbox
                        checked={(basisData.length > 0 ? basisData.length : sites.length) > 0 && selectedSites.length === (basisData.length > 0 ? basisData.length : sites.length)}
                        indeterminate={selectedSites.length > 0 && selectedSites.length < (basisData.length > 0 ? basisData.length : sites.length)}
                        sx={{
                          p: 0,
                          color: colors.neutral[400],
                          "&.Mui-checked": { color: "#6371F7" },
                          "&.MuiCheckbox-indeterminate": { color: "#6371F7" },
                        }}
                      />
                      <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                        Websites ({selectedSites.length}/{basisData.length > 0 ? basisData.length : sites.length})
                      </Typography>
                    </Stack>
                    {(basisData.length > 0 ? basisData : sites).map((site) => {
                      const siteId = site.site_id || site.id;
                      const siteName = site.site_id || site.name || site.domain;
                      return (
                        <Stack
                          key={siteId}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          onClick={() => handleSiteToggle(siteId)}
                          sx={{ py: 1, cursor: "pointer" }}
                        >
                          <Checkbox
                            checked={selectedSites.includes(siteId)}
                            sx={{
                              p: 0,
                              color: colors.neutral[400],
                              "&.Mui-checked": { color: "#6371F7" },
                            }}
                          />
                          <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: colors.neutral[800] }}>
                            {siteName}
                          </Typography>
                        </Stack>
                      );
                    })}
                    {(basisData.length === 0 && sites.length === 0) && (
                      <Typography sx={ { fontSize: 13, color: colors.neutral[500], py: 2 }}>
                        No websites available
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "8px",
                    backgroundColor: "rgba(99, 113, 247, 0.08)",
                    border: "1px solid rgba(99, 113, 247, 0.2)",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <LayerdIcon icon={UI_ICONS.info} size={20} color="primary" style={{ flexShrink: 0, marginTop: 2 }} />
                    <Typography sx={ { fontSize: 13, lineHeight: 1.6, color: "#6371F7" }}>
                      Event logs will be sent to your {webhook.name} endpoint in real-time. 
                      Make sure your endpoint is properly configured to receive webhook payloads.
                    </Typography>
                  </Stack>
                </Box>
                </Stack>
              )}
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{ p: 4, borderTop: `1px solid ${colors.neutral[200]}` }}
            >
              <Button
                onClick={handleTestConnection}
                disabled={testStatus === "testing" || loading || saving || !validation.valid}
                sx={{
                  height: 44,
                  flex: 1,
                  borderRadius: "8px",
                  fontSize: 14,
                  color: testStatus === "success" ? "#065F46" : testStatus === "error" ? "#DC2626" : "#6371F7",
                  fontWeight: "medium",
                  textTransform: "none",
                  border: `1px solid ${testStatus === "success" ? "#065F46" : testStatus === "error" ? "#DC2626" : "#6371F7"}`,
                  backgroundColor: testStatus === "success" ? "#D1FAE5" : testStatus === "error" ? "#FEE2E2" : "transparent",
                  "&:hover": {
                    backgroundColor: testStatus === "success" ? "#D1FAE5" : testStatus === "error" ? "#FEE2E2" : "rgba(99, 113, 247, 0.08)",
                  },
                }}
              >
                {testStatus === "testing" ? "Testing..." : testStatus === "success" ? "Connection OK" : testStatus === "error" ? "Connection Failed" : "Test Connection"}
              </Button>
              <SaveButton
                onClick={handleSave}
                disabled={loading || saving || !validation.valid}
                loading={saving}
                label={saving ? "Saving..." : "Save"}
                sx={{ ...formSaveButtonSx, flex: 1, width: "auto" }}
              />
            </Stack>
          </ReactPerfectScrollbar>
        </Box>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};

const ExternalWebhooksPage = () => {
  const { sites } = useSite();
  const { getWebhook, webhook: webhookData, setWebhook: setWebhookStatus } = useAdmin();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const alertIntegrations = useAlertIntegrations();
  const [webhooks, setWebhooks] = useState(webhookTypes);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [basisData, setBasisData] = useState([]);
  
  // Check permissions
  const canConfigureWebhooks = hasPermission(PERMISSIONS.WEBHOOKS_WRITE);

  useEffect(() => {
    fetchWebhookData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (webhookData && Array.isArray(webhookData)) {
      const updatedWebhooks = webhookTypes.map((webhook) => {
        let webhookConfig = webhookData.find((w) => w?.type === webhook.externalLogType);
        if (!webhookConfig && webhookData[webhook.externalLogType]) {
          webhookConfig = webhookData[webhook.externalLogType];
        }
        if (!webhookConfig && typeof webhookData[webhook.externalLogType] === "boolean") {
          webhookConfig = { enabled: webhookData[webhook.externalLogType] };
        }
        return {
          ...webhook,
          enabled: webhookConfig?.enabled || false,
        };
      });
      setWebhooks(updatedWebhooks);
      setLoading(false);
    } else if (webhookData === null) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [webhookData]);

  const fetchWebhookData = async () => {
    setLoading(true);
    try {
      await getWebhook();
    } catch (err) {
      console.error("Failed to fetch webhook data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookLogoClick = async (webhook) => {
    const webhookIndex = webhookTypes.findIndex((w) => w.id === webhook.id);
    if (webhookIndex >= 3) {
      return;
    }
    
    // Open drawer immediately
    setSelectedWebhook(webhook);
    setDrawerOpen(true);

    try {
      const [, , basisResponse] = await Promise.all([
        axios.get("/auth"),
        axios.get("/auth/features"),
        axios.get("/site/basis"),
      ]);
      
      if (basisResponse.data) {
        const basisSites = Array.isArray(basisResponse.data) 
          ? basisResponse.data 
          : [basisResponse.data];
        setBasisData(basisSites);
      }
    } catch (err) {
      console.error("Failed to load initial data:", err);
      setBasisData([]);
    }
  };

  const handleStatusToggle = async (webhook) => {
    if (!webhook?.externalLogType) return;
    
    const webhookIndex = webhookTypes.findIndex((w) => w.id === webhook.id);
    if (webhookIndex >= 3) {
      return;
    }
    
    const newEnabled = !webhook.enabled;
    
    try {
      await setWebhookStatus(webhook.externalLogType, newEnabled);
      
      setWebhooks((prev) =>
        prev.map((w) =>
          w.id === webhook.id ? { ...w, enabled: newEnabled } : w
        )
      );
      
      // Refresh webhook data
      await fetchWebhookData();
    } catch (err) {
      console.error("Failed to update webhook status:", err);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedWebhook(null);
    setBasisData([]); 
  };

  const handleWebhookSave = () => {
    fetchWebhookData();
  };

  const integrationsLoading = loading || alertIntegrations.loading;

  return (
    <PermissionGate permission={PERMISSIONS.WEBHOOKS_READ} resourceLabel="webhooks configuration">
    <>
      <Helmet title="Integrations" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <Stack spacing={2}>
            <V2PageHeader
              title="Integrations"
              description="Forward event logs to SIEM platforms and route alerts to PagerDuty, Slack, Jira, and other incident systems."
              sx={{ pb: 0 }}
            />
          </Stack>

          <Box
            sx={{
              p: 4,
              borderRadius: "12px",
              backgroundColor: colors.neutral[50],
              border: `1px solid ${colors.neutral[200]}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: "medium",
                color: colors.neutral[1000],
                mb: 3,
              }}
            >
              Available Integrations
            </Typography>
            {alertIntegrations.err && (
              <Alert severity="error" sx={{ mb: 2 }}>{alertIntegrations.err}</Alert>
            )}
            {integrationsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.neutral[1000],
                    mb: 2,
                  }}
                >
                  Log forwarding
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 3,
                  }}
                >
                  {webhooks.map((webhook, index) => (
                    <WebhookCard
                      key={webhook.id}
                      webhook={webhook}
                      webhookIndex={index}
                      onLogoClick={() => handleWebhookLogoClick(webhook)}
                      onStatusToggle={() => handleStatusToggle(webhook)}
                    />
                  ))}
                </Box>
                <PermissionGate permission="ALERT">
                  <AlertVendorGroups
                    supportedKinds={alertIntegrations.kinds}
                    destinationsForKind={alertIntegrations.destinationsForKind}
                    onOpenVendor={alertIntegrations.openVendor}
                  />
                </PermissionGate>
                <PermissionGate permission="ALERT">
                  <ConfiguredDestinationsList
                    items={alertIntegrations.items}
                    testing={alertIntegrations.testing}
                    onTest={alertIntegrations.onTest}
                    onEdit={(it) => alertIntegrations.setEditor({ mode: "edit", kind: it.kind, existing: it })}
                    onDelete={alertIntegrations.onDelete}
                    onDeliveries={alertIntegrations.setDeliveriesFor}
                  />
                </PermissionGate>
              </>
            )}
          </Box>
        </Stack>
      </Box>

      <PermissionGate permission="ALERT">
        <AlertIntegrationOverlays
          editor={alertIntegrations.editor}
          setEditor={alertIntegrations.setEditor}
          onSaved={alertIntegrations.onSaved}
          deliveriesFor={alertIntegrations.deliveriesFor}
          setDeliveriesFor={alertIntegrations.setDeliveriesFor}
        />
      </PermissionGate>

      <WebhookConfigDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        webhook={selectedWebhook}
        sites={sites || []}
        basisData={basisData}
        onSave={handleWebhookSave}
      />
    </>
    </PermissionGate>
  );
};

export default ExternalWebhooksPage;
